import { useState, useEffect, useCallback } from 'react';
import { supabase, saveCompensation, upsertScheduleException } from '../lib/supabase';
import { getHourlyConfig, saveHourlyConfig, listSettlements, computeHourly, createSettlement, markSettlementPaid, ratesFor } from '../lib/hourly';
import { openHourlyComprobante, hourlyMail } from '../lib/comprobante';
import { sendReceiptMail } from '../lib/org';

const money = (n) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n || 0);
const field = 'w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sky-300';
const iso = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const hm = (m) => `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')} min`;

const Sheet = ({ title, sub, onClose, children, wide }) => (
  <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
    <div className={`bg-white w-full ${wide ? 'sm:max-w-3xl' : 'sm:max-w-md'} rounded-t-3xl sm:rounded-3xl max-h-[90vh] overflow-y-auto p-5 space-y-4`} onClick={(e) => e.stopPropagation()}>
      <div className="flex items-start justify-between gap-3">
        <div><h3 className="text-lg font-bold text-gray-900">{title}</h3>{sub && <p className="text-xs text-gray-400">{sub}</p>}</div>
        <button onClick={onClose} className="text-gray-400 text-xl leading-none">×</button>
      </div>
      {children}
    </div>
  </div>
);

// Tipo de empleado, área y tarifa
export function HourlyProfileModal({ emp, cfg, onClose, onSaved, showToast }) {
  const [type, setType] = useState(emp.pay_type || 'fixed');
  const [area, setArea] = useState(emp.area || '');
  const [rate, setRate] = useState(emp.hourly_rate ?? '');
  const [perDiem, setPerDiem] = useState(emp.per_diem ?? '');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { error } = await supabase.from('profiles').update({ pay_type: type, area: area || null }).eq('id', emp.id);
      if (error) throw error;
      if (type === 'hourly') await saveCompensation(emp.id, { hourly_rate: rate === '' ? null : Number(rate), per_diem: perDiem === '' ? null : Number(perDiem) });
      showToast?.('Datos guardados'); await onSaved?.(); onClose();
    } catch (e) { showToast?.(e.message || 'No se pudo guardar', 'error'); }
    setBusy(false);
  };
  return (
    <Sheet title={`Tipo y tarifa — ${emp.name}`} onClose={onClose}>
      <div className="grid grid-cols-2 gap-2">
        {[['fixed', 'Fijo', 'Horario y sueldo mensual'], ['hourly', 'Por hora', 'Esporádico, paga por hora']].map(([v, l, d]) => (
          <button key={v} type="button" onClick={() => setType(v)} className={`text-left rounded-2xl border-2 p-3 ${type === v ? 'border-sky-500 bg-sky-50' : 'border-gray-100'}`}>
            <p className="text-sm font-bold text-gray-900">{l}</p><p className="text-xs text-gray-400">{d}</p>
          </button>
        ))}
      </div>
      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide">Perfil / área de trabajo
        <select className={`${field} mt-1 font-normal normal-case`} value={area} onChange={(e) => setArea(e.target.value)}>
          <option value="">Sin área</option>
          {cfg.areas.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
      </label>
      {type === 'hourly' && (
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Valor por hora
            <input type="number" min="0" className={`${field} mt-1 font-normal`} value={rate} onChange={(e) => setRate(e.target.value)} placeholder={ratesFor(cfg, area).rate ? `${area ? 'Del perfil' : 'Predet.'}: ${money(ratesFor(cfg, area).rate)}` : 'Ej: 8500'} />
          </label>
          <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Viático por día
            <input type="number" min="0" className={`${field} mt-1 font-normal`} value={perDiem} onChange={(e) => setPerDiem(e.target.value)} placeholder={ratesFor(cfg, area).perDiem ? `${area ? 'Del perfil' : 'Predet.'}: ${money(ratesFor(cfg, area).perDiem)}` : 'Ej: 5000'} />
          </label>
          <p className="col-span-2 text-xs text-gray-400">Vacío = usa el valor del perfil (área) elegido o, si no tiene, el general de Configuración. No necesita horario fijo: marca cuando viene y se le paga por los minutos trabajados.</p>
        </div>
      )}
      <button disabled={busy} onClick={save} className="w-full py-3 rounded-2xl text-sm font-bold text-white bg-sky-700 disabled:opacity-50">Guardar</button>
    </Sheet>
  );
}

// Perfiles de pago por hora (Configuración): categorías con su valor por hora y viático, editables
export function HourlyConfigPanel({ orgId, showToast }) {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => getHourlyConfig().then((c) => { setCfg({ ...c, profiles: c.profiles.map((p) => ({ ...p, _orig: p.name })) }); }).catch(() => showToast?.('No se pudo cargar', 'error')), [showToast]);
  useEffect(() => { load(); }, [load]);
  if (!cfg) return <p className="text-sm text-gray-400">Cargando…</p>;
  const setP = (i, k, v) => setCfg({ ...cfg, profiles: cfg.profiles.map((p, x) => (x === i ? { ...p, [k]: v } : p)) });
  const move = (i, d) => { const j = i + d; if (j < 0 || j >= cfg.profiles.length) return; const l = [...cfg.profiles]; [l[i], l[j]] = [l[j], l[i]]; setCfg({ ...cfg, profiles: l }); };
  const add = () => setCfg({ ...cfg, profiles: [...cfg.profiles, { name: '', rate: '', per_diem: '', _new: true }] });
  const del = (i) => { if (window.confirm('¿Quitar este perfil? Las personas que lo tienen conservan el área pero pasan al valor general.')) setCfg({ ...cfg, profiles: cfg.profiles.filter((_, x) => x !== i) }); };
  const save = async () => {
    const names = cfg.profiles.map((p) => p.name.trim()).filter(Boolean);
    if (new Set(names.map((n) => n.toLowerCase())).size !== names.length) { showToast?.('Hay perfiles con el mismo nombre', 'error'); return; }
    setBusy(true);
    try {
      // renombres: las personas con el nombre viejo pasan al nuevo
      for (const p of cfg.profiles) {
        const n = p.name.trim();
        if (p._orig && n && p._orig !== n) {
          const { error } = await supabase.from('profiles').update({ area: n }).eq('area', p._orig);
          if (error) throw error;
        }
      }
      await saveHourlyConfig(orgId, cfg); showToast?.('Perfiles guardados'); await load();
    } catch (e) { showToast?.(e.message, 'error'); }
    setBusy(false);
  };
  return (
    <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-5 space-y-5">
      <div><h3 className="font-bold text-gray-900 text-sm">Perfiles de pago por hora</h3>
        <p className="text-xs text-gray-400 mt-0.5">Cada perfil es una categoría de trabajo (Limpieza, Docente…) con su valor por hora y viático por día. Al crear una persona por hora elegís su perfil; si necesita otra tarifa, se la cambiás solo a ella.</p></div>
      <div className="space-y-3">
        {cfg.profiles.map((p, i) => (
          <div key={i} className="rounded-2xl border border-gray-100 bg-gray-50/60 p-3 space-y-2">
            <div className="flex gap-2 items-center">
              <input className={field} placeholder="Nombre del perfil (ej: Limpieza)" value={p.name} onChange={(e) => setP(i, 'name', e.target.value)} />
              <button type="button" onClick={() => move(i, -1)} aria-label="Subir" className="w-9 h-9 rounded-xl bg-white border border-gray-200 text-gray-500 text-xs flex-shrink-0">▲</button>
              <button type="button" onClick={() => move(i, 1)} aria-label="Bajar" className="w-9 h-9 rounded-xl bg-white border border-gray-200 text-gray-500 text-xs flex-shrink-0">▼</button>
              <button type="button" onClick={() => del(i)} aria-label="Quitar" className="w-9 h-9 rounded-xl bg-white border border-red-100 text-red-400 flex-shrink-0">×</button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wide">Valor por hora
                <input type="number" min="0" className={`${field} mt-1 font-normal`} value={p.rate ?? ''} onChange={(e) => setP(i, 'rate', e.target.value)} placeholder={cfg.default_rate ? `General: ${money(cfg.default_rate)}` : 'Sin definir'} /></label>
              <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wide">Viático por día
                <input type="number" min="0" className={`${field} mt-1 font-normal`} value={p.per_diem ?? ''} onChange={(e) => setP(i, 'per_diem', e.target.value)} placeholder={cfg.default_per_diem ? `General: ${money(cfg.default_per_diem)}` : 'Sin definir'} /></label>
            </div>
          </div>
        ))}
        <button type="button" onClick={add} className="w-full py-2.5 rounded-2xl text-sm font-bold text-sky-700 bg-sky-50 border border-sky-100">+ Nuevo perfil</button>
      </div>
      <div className="border-t border-gray-100 pt-4">
        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Valores generales</p>
        <p className="text-xs text-gray-400 mb-2">Se usan cuando el perfil (o la persona) no define un valor.</p>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wide">Valor por hora
            <input type="number" min="0" className={`${field} mt-1 font-normal`} value={cfg.default_rate} onChange={(e) => setCfg({ ...cfg, default_rate: e.target.value })} /></label>
          <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wide">Viático por día
            <input type="number" min="0" className={`${field} mt-1 font-normal`} value={cfg.default_per_diem} onChange={(e) => setCfg({ ...cfg, default_per_diem: e.target.value })} /></label>
        </div>
      </div>
      <button disabled={busy} onClick={save} className="w-full py-2.5 rounded-xl text-sm font-bold text-white bg-sky-700 disabled:opacity-50">Guardar perfiles</button>
    </div>
  );
}

// Convocar: avisa a alguien por hora que lo esperan un día y en qué horario
export function ConvocarModal({ emp, adminId, onClose, showToast }) {
  const [date, setDate] = useState(iso(new Date(Date.now() + 86400000)));
  const [from, setFrom] = useState('09:00');
  const [to, setTo] = useState('15:00');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { await upsertScheduleException({ employeeId: emp.id, date, type: 'custom', startTime: from, endTime: to, note, createdBy: adminId }); showToast?.('Convocatoria enviada'); onClose(); }
    catch (e) { showToast?.(e.message || 'No se pudo convocar', 'error'); }
    setBusy(false);
  };
  return (
    <Sheet title={`Convocar a ${emp.name}`} sub="Recibe un aviso y, si usás el horario habilitado, solo podrá marcar en ese turno." onClose={onClose}>
      <input type="date" className={field} value={date} min={iso(new Date())} onChange={(e) => setDate(e.target.value)} />
      <div className="grid grid-cols-2 gap-2"><input type="time" className={field} value={from} onChange={(e) => setFrom(e.target.value)} /><input type="time" className={field} value={to} onChange={(e) => setTo(e.target.value)} /></div>
      <input className={field} placeholder="Nota (opcional)" value={note} onChange={(e) => setNote(e.target.value)} />
      <button disabled={busy} onClick={send} className="w-full py-3 rounded-2xl text-sm font-bold text-white bg-sky-700 disabled:opacity-50">Convocar</button>
    </Sheet>
  );
}

// Liquidar por horas: día, semana, mes o rango
export function HourlySettlementsButton({ employees, adminId, orgName, showToast, canEdit = true, onChange }) {
  const hourly = employees.filter((e) => e.pay_type === 'hourly');
  const [open, setOpen] = useState(false);
  const today = new Date();
  const monday = new Date(today); monday.setDate(today.getDate() - ((today.getDay() + 6) % 7));
  const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
  const [from, setFrom] = useState(iso(monday));
  const [to, setTo] = useState(iso(sunday));
  const [rows, setRows] = useState([]);
  const [done, setDone] = useState([]);
  const [bonus, setBonus] = useState({});
  const [busy, setBusy] = useState(false);

  const preset = (k) => {
    const t = new Date();
    if (k === 'day') { setFrom(iso(t)); setTo(iso(t)); }
    if (k === 'week') { const m = new Date(t); m.setDate(t.getDate() - ((t.getDay() + 6) % 7)); const s = new Date(m); s.setDate(m.getDate() + 6); setFrom(iso(m)); setTo(iso(s)); }
    if (k === 'month') { setFrom(iso(new Date(t.getFullYear(), t.getMonth(), 1))); setTo(iso(new Date(t.getFullYear(), t.getMonth() + 1, 0))); }
  };
  const load = useCallback(async () => {
    if (!hourly.length || !from || !to || to < from) return;
    try {
      const cfg = await getHourlyConfig();
      const comp = Object.fromEntries(hourly.map((e) => [e.id, { hourly_rate: e.hourly_rate, per_diem: e.per_diem }]));
      setRows(await computeHourly({ employees: hourly, comp, cfg, from, to }));
      setDone(await listSettlements({ from, to }));
    } catch (e) { showToast?.(e.message, 'error'); }
  }, [hourly.map((e) => e.id).join(','), from, to]); // eslint-disable-line
  useEffect(() => { if (open) load(); }, [open, load]);

  const run = async (fn, ok) => { setBusy(true); try { await fn(); showToast?.(ok); await load(); onChange?.(); } catch (e) { showToast?.(e.message, 'error'); } setBusy(false); };
  const pendingTotal = done.filter((s) => s.status === 'approved').reduce((a, s) => a + Number(s.total), 0);
  const paidTotal = done.filter((s) => s.status === 'paid').reduce((a, s) => a + Number(s.total), 0);

  if (!hourly.length) return null;
  return (
    <>
      <button onClick={() => setOpen(true)} className="px-4 py-2.5 rounded-2xl text-sm font-bold border border-gray-200 text-gray-600 bg-white hover:bg-gray-50">Liquidar por horas</button>
      {open && (
        <Sheet wide title="Liquidación por horas" sub="Se paga por los minutos trabajados, más el viático por cada día trabajado." onClose={() => setOpen(false)}>
          <div className="flex flex-wrap gap-2 items-center">
            {[['day', 'Hoy'], ['week', 'Semana'], ['month', 'Mes']].map(([k, l]) => <button key={k} onClick={() => preset(k)} className="px-3 py-1.5 rounded-full text-xs font-bold bg-gray-100 text-gray-600">{l}</button>)}
            <input type="date" className="border border-gray-200 rounded-xl px-2.5 py-1.5 text-sm" value={from} onChange={(e) => setFrom(e.target.value)} />
            <span className="text-gray-300">–</span>
            <input type="date" className="border border-gray-200 rounded-xl px-2.5 py-1.5 text-sm" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>

          <div className="space-y-3">
            {rows.map((r) => (
              <div key={r.emp.id} className="rounded-2xl border border-gray-100 p-3.5">
                <div className="flex items-start justify-between gap-2">
                  <div><p className="text-sm font-bold text-gray-900">{r.emp.name}{r.emp.area && <span className="ml-2 text-xs font-semibold text-sky-700 bg-sky-50 px-2 py-0.5 rounded-full">{r.emp.area}</span>}</p>
                    <p className="text-xs text-gray-400">{hm(r.minutes)} × {money(r.rate)}/h · {r.days} día{r.days === 1 ? '' : 's'} × {money(r.perDiem)}</p></div>
                  <p className="text-base font-black text-emerald-600">{money(r.hoursAmt + r.perDiemAmt - r.advAmt)}</p>
                </div>
                {r.incomplete > 0 && <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-2.5 py-1.5 mt-2">{r.incomplete} jornada{r.incomplete > 1 ? 's' : ''} sin salida: no se cuenta{r.incomplete > 1 ? 'n' : ''} hasta que la corrijas en Registros.</p>}
                {r.rate === 0 && <p className="text-xs text-red-600 mt-2">Sin valor por hora. Cargalo en la tarjeta de la persona o en Configuración.</p>}
                {r.advAmt > 0 && <p className="text-xs text-red-600 mt-1">Incluye adelantos por {money(r.advAmt)}.</p>}
                {canEdit && r.minutes > 0 && r.rate > 0 && (
                  <div className="flex gap-2 mt-3">
                    <input type="number" min="0" placeholder="Bonus (opcional)" className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm" value={bonus[r.emp.id] || ''} onChange={(e) => setBonus({ ...bonus, [r.emp.id]: e.target.value })} />
                    <button disabled={busy} onClick={() => run(() => createSettlement({ row: r, from, to, bonus: bonus[r.emp.id], adminId }), 'Liquidación creada')} className="px-4 rounded-xl text-sm font-bold text-white bg-sky-700 disabled:opacity-50">Liquidar</button>
                  </div>
                )}
                {r.minutes === 0 && r.incomplete === 0 && <p className="text-xs text-gray-400 mt-2">Sin horas pendientes en este período.</p>}
              </div>
            ))}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2"><p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Liquidaciones del período</p>
              <p className="text-xs text-gray-500">Pagado {money(paidTotal)} · Por pagar {money(pendingTotal)}</p></div>
            {done.length === 0 ? <p className="text-xs text-gray-400">Todavía no hay liquidaciones en estas fechas.</p> : (
              <div className="divide-y divide-gray-50">
                {done.map((s) => (
                  <div key={s.id} className="py-2.5 flex items-center gap-2 flex-wrap">
                    <div className="flex-1 min-w-[150px]"><p className="text-sm font-bold text-gray-800">{s.employee?.name} <span className="text-emerald-600">{money(s.total)}</span></p>
                      <p className="text-xs text-gray-400">{s.period_from} al {s.period_to}</p></div>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-lg ${s.status === 'paid' ? 'bg-teal-100 text-teal-700' : 'bg-amber-100 text-amber-700'}`}>{s.status === 'paid' ? 'Pagado' : 'Falta pagar'}</span>
                    {canEdit && <button disabled={busy} onClick={() => run(() => markSettlementPaid(s.id, adminId, s.status !== 'paid'), s.status === 'paid' ? 'Vuelto a pendiente' : 'Marcado como pagado')} className="px-2.5 py-1 text-xs font-bold rounded-lg bg-teal-50 text-teal-700">{s.status === 'paid' ? 'Deshacer' : 'Marcar pagado'}</button>}
                    {s.status === 'paid' && <button onClick={() => openHourlyComprobante({ orgName, empName: s.employee?.name, s })} className="px-2.5 py-1 text-xs font-bold rounded-lg bg-sky-50 text-sky-700">Comprobante{s.receipt_no ? ` · ${s.receipt_no}` : ''}</button>}
                    {s.status === 'paid' && canEdit && <button disabled={busy} onClick={() => { if (!window.confirm('¿Enviar el comprobante por mail a ' + s.employee?.name + '?')) return; const m = hourlyMail({ orgName, empName: s.employee?.name, s }); run(() => sendReceiptMail(s.employee_id, m.subject, m.html), 'Comprobante enviado'); }} className="px-2.5 py-1 text-xs font-bold rounded-lg bg-gray-50 text-gray-600">Enviar por mail</button>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </Sheet>
      )}
    </>
  );
}

// Empleado por hora: ve sus liquidaciones
export function EmployeeSettlements({ employeeId, empName, orgName }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { listSettlements({ employeeId }).then(setRows).catch(() => {}); }, [employeeId]);
  if (!rows.length) return null;
  return (
    <div className="bg-white rounded-3xl border border-gray-100 p-5 space-y-2">
      <h3 className="font-bold text-gray-900 text-sm">Mis pagos por horas</h3>
      {rows.slice(0, 10).map((s) => (
        <div key={s.id} className="flex items-center justify-between gap-2 py-1.5">
          <div><p className="text-sm font-bold text-gray-800">{money(s.total)}</p><p className="text-xs text-gray-400">{s.period_from} al {s.period_to}</p></div>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-bold px-2 py-0.5 rounded-lg ${s.status === 'paid' ? 'bg-teal-100 text-teal-700' : 'bg-amber-100 text-amber-700'}`}>{s.status === 'paid' ? 'Pagado' : 'Pendiente'}</span>
            {s.status === 'paid' && <button onClick={() => openHourlyComprobante({ orgName, empName, s })} className="text-xs font-bold text-sky-700">Comprobante</button>}
          </div>
        </div>
      ))}
    </div>
  );
}
